import React from "react";

export type ActionButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "danger"
  | "amber"
  | "neutral";

interface ActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ActionButtonVariant;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
  /** Leading icon — an `st-*` <i>, a lucide icon, or an emoji. */
  icon?: React.ReactNode;
  /** Stretch to the full width of the parent (used in mobile card rows). */
  fullWidth?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
}

const VARIANT_CLASSES: Record<ActionButtonVariant, string> = {
  primary: "bg-light-yellow text-white hover:opacity-90",
  secondary: "bg-gray-100 text-grease-200 hover:bg-gray-200",
  outline:
    "border border-light-yellow text-light-yellow bg-white hover:bg-yellow-50",
  danger: "bg-red-500 text-white hover:bg-red-600",
  amber: "bg-light-yellow text-white hover:opacity-90",
  neutral:
    "border border-[#D1D5DB] text-grease-100 bg-white hover:bg-gray-50",
};

/**
 * Reusable Action Button
 * Compact labelled button for admin tables, section headers and modal footers.
 */
const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  onClick,
  variant = "primary",
  type = "button",
  disabled = false,
  loading = false,
  icon,
  fullWidth = false,
  className = "",
  title,
  ariaLabel,
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    title={title}
    aria-label={ariaLabel || title}
    className={`appearance-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 ${
      VARIANT_CLASSES[variant]
    } ${fullWidth ? "w-full" : ""} ${
      disabled || loading
        ? "opacity-60 cursor-not-allowed pointer-events-none"
        : ""
    } ${className}`}
  >
    {loading ? (
      // Fixed-size, non-animated wrapper so iOS Safari's flexbox keeps this
      // item's box out of the way of the label text — without it, Safari can
      // render the rotating (transformed) svg on top of the following text.
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
        <svg
          className="animate-spin h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </span>
    ) : (
      icon && (
        <span className="shrink-0 leading-none" aria-hidden="true">
          {icon}
        </span>
      )
    )}
    {/* Wrapped rather than left as a bare text node. A raw string inside a flex
        container becomes an anonymous flex item, and iOS Safari doesn't give
        those a proper box — so the label could be painted underneath the
        transformed (spinning) svg beside it, which is what put the loader on
        top of the first letter. The spinner's own wrapper above fixed only its
        half of that. */}
    <span className="min-w-0">{children}</span>
  </button>
);

export default ActionButton;
